import { fetch } from './fetch';
import { addTruthyParamsToUrl } from '@zapier/url-utils';
import { urls } from '@zapier/identity-utils';
import { ID } from '../types';
import isEmpty from 'lodash/isEmpty';

export enum DomainAccessPolicyType {
  auto_join = 'auto_join',
  invite_only = 'invite_only',
}

export type DiscoveredDomainAccount = {
  id: ID;
  name: string;
  logoUrl: string;
  accessPolicy: DomainAccessPolicyType;
  ssoUrl: string | null;
  hasSamlSso: boolean;
  domain: string;
  isDomainCaptureBlocked: boolean;
};

export type DomainDiscoveryUser = {
  id: ID | null;
};

export type DiscoveredDomain = {
  account: DiscoveredDomainAccount | null;
  user: DomainDiscoveryUser | null;
  errors: any[] | null;
};

export type AccountResponse = {
  id: ID;
  name: string;
  logo_url: string;
  access_policy: DomainAccessPolicyType;
  sso_url: string | null;
  has_saml_sso: boolean;
  is_domain_capture_blocked: boolean;
};

export type DomainDiscoveryResponse = {
  account?: AccountResponse;
  user?: DomainDiscoveryUser;
  errors?: any[];
};

/**
 * Retrieve account information and SAML login url
 * for accounts eligible for domain capture.
 *
 * @param email
 */
export async function discoverDomain({
  email,
}: {
  email: string;
}): Promise<DiscoveredDomain | null> {
  let account;
  let user;
  let errors;

  if (!isEmpty(email)) {
    const domainDiscoveryData = await fetch<DomainDiscoveryResponse>(
      addTruthyParamsToUrl(urls.DOMAIN_DISCOVERY_URL, { email })
    );

    account = domainDiscoveryData.body?.account;
    user = domainDiscoveryData.body?.user;
    errors = domainDiscoveryData.body?.errors;

    if (errors == null && domainDiscoveryData.status > 399) {
      throw (
        'Error occurred calling ' +
        urls.DOMAIN_DISCOVERY_URL +
        ' response.status: ' +
        domainDiscoveryData.status +
        ' response.body: ' +
        domainDiscoveryData.body
      );
    }
  }

  return {
    account: account
      ? {
          id: account.id,
          name: account.name,
          logoUrl: account.logo_url,
          accessPolicy: account.access_policy,
          ssoUrl: account.sso_url,
          hasSamlSso: account.has_saml_sso,
          domain: email.split('@')[1],
          isDomainCaptureBlocked: account.is_domain_capture_blocked,
        }
      : null,
    user: user ?? null,
    errors: errors ?? null,
  };
}
